import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Snackbar,
  CircularProgress,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Container,
  Divider,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Update as UpdateIcon
} from '@material-ui/icons';
import { useLocation, useHistory } from 'react-router-dom';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { api } from '@services/api';
import { AxiosError } from 'axios';
import { makeStyles, withStyles } from '@material-ui/core/styles';

// Estilos customizados
const useStyles = makeStyles((theme) => ({
  root: {
    padding: '24px 0',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh'
  },
  paperContainer: {
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  headerTitle: {
    marginBottom: '32px',
    fontWeight: 600,
    color: '#2c3e50',
    position: 'relative',
    paddingBottom: '16px',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '80px',
      height: '4px',
      backgroundColor: '#0D9C5B',
      borderRadius: '2px'
    }
  },
  section: {
    marginBottom: '32px',
    padding: '16px',
    borderLeft: '4px solid #0D9C5B',
    backgroundColor: '#f0f7f4'
  },
  sectionSecondary: {
    borderLeft: '4px solid #2196F3',
    backgroundColor: '#f0f7ff'
  },
  tableContainer: {
    maxHeight: 400,
    overflow: 'auto',
    marginTop: theme.spacing(2),
    borderRadius: '8px'
  },
  compactCell: {
    padding: theme.spacing(1)
  },
  highlightRow: {
    backgroundColor: theme.palette.action.hover
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    marginTop: '32px',
    paddingTop: '16px',
    borderTop: '1px solid #e0e0e0'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4)
  },
  loadingContent: {
    textAlign: 'center',
    padding: theme.spacing(4),
    borderRadius: theme.spacing(1),
    backgroundColor: '#fff',
    boxShadow: theme.shadows[4]
  }
}));

const ButtonAdd = withStyles(() => ({
  root: {
    background: '#0D9C5B',
    color: '#FFF',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 500,
    padding: '8px 16px',
    textTransform: 'none',
    '&:hover': {
      background: '#FFF',
      borderColor: '#0D9C5B',
      color: '#0D9C5B',
      border: 'solid 1px #0D9C5B'
    }
  }
}))(Button);

interface IEmpresa {
  idcad_empresa: number;
  num_cnpj: string;
  nome_empresa: string;
  cod_contrato: string;
}

interface IGheAtualizacao {
  cod_contrato: string;
  cod_ghe: string;
  cod_local: string;
  cod_obra: string;
  nome_empresa: string;
  nome_ghe: string;
  num_cnpj: string;
  novo_cod_contrato?: string;
  novo_cod_ghe?: string;
  novo_cod_local?: string;
}

interface ApiErrorResponse {
  error?: string;
  message?: string;
  errors?: Record<string, string>;
}

interface ILoadingState {
  inicial: boolean;
  enviando: boolean;
}

const Alert = (props: AlertProps) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const NovoContratoEmpresas = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const empresaSelecionada = location.state as IEmpresa;

  // Estados para os dados
  const [cnpj, setCnpj] = useState<string>(empresaSelecionada.num_cnpj);
  const [nomeEmpresa, setNomeEmpresa] = useState<string>(
    empresaSelecionada.nome_empresa
  );
  const [codContratoAtual, setCodContratoAtual] = useState<string>(
    empresaSelecionada.cod_contrato
  );
  const [codigoDigitado, setCodigoDigitado] = useState<string>('');
  const [localObraNovo, setLocalObraNovo] = useState<string>('');
  const [ghesParaAtualizar, setGhesParaAtualizar] = useState<IGheAtualizacao[]>(
    []
  );
  const [dadosCarregados, setDadosCarregados] = useState<boolean>(false);

  // Estados para feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'error' | 'warning'
  >('success');
  const [loading, setLoading] = useState<ILoadingState>({
    inicial: true,
    enviando: false
  });
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  // Carrega os dados ao montar o componente
  useEffect(() => {
    const carregarDados = async () => {
      setLoading(prev => ({ ...prev, inicial: true }));
      try {
        const response = await api.post('/ravim/novo-contr-emp', { cnpj });

        if (response.data?.error) {
          showSnackbar(response.data.error, 'error');
          return;
        }

        if (response.data && Array.isArray(response.data)) {
          const dadosGhes = response.data as IGheAtualizacao[];
          
          if (dadosGhes.length > 0) {
            const primeiroGhe = dadosGhes[0];
            setNomeEmpresa(primeiroGhe.nome_empresa);
            setCodContratoAtual(primeiroGhe.cod_contrato);
            setGhesParaAtualizar(dadosGhes);
          } else {
            // Se não houver GHEs, mantemos os dados da empresa selecionada
            setNomeEmpresa(empresaSelecionada.nome_empresa);
            setCodContratoAtual(empresaSelecionada.cod_contrato);
            setGhesParaAtualizar([]);
          }

          setDadosCarregados(true);
        } else {
          showSnackbar('Nenhum GHE cadastrado - apenas o contrato será atualizado', 'warning');
          setGhesParaAtualizar([]);
          setDadosCarregados(true);
        }
      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        if (error.response?.data?.error) {
          showSnackbar(error.response.data.error, 'error');
        } else {
          showSnackbar('Erro ao carregar dados dos GHEs', 'error');
        }
        setGhesParaAtualizar([]);
        setDadosCarregados(true);
      } finally {
        setLoading(prev => ({ ...prev, inicial: false }));
      }
    };

    carregarDados();
  }, [cnpj]);

  // Atualiza os novos valores quando o código digitado muda
  const handleCodigoDigitadoChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const novoCodigo = e.target.value;
    setCodigoDigitado(novoCodigo);

    if (dadosCarregados && novoCodigo) {
      // Atualiza o local de obra novo baseado no código digitado e nome da empresa
      setLocalObraNovo(`${novoCodigo}-${nomeEmpresa}`);
      
      // Se houver GHEs para atualizar, atualiza também os códigos dos GHEs
      if (ghesParaAtualizar.length > 0) {
        const novosGhes = ghesParaAtualizar.map((ghe) => ({
          ...ghe,
          novo_cod_contrato: novoCodigo,
          novo_cod_ghe: `${novoCodigo}-${ghe.nome_empresa}-${ghe.nome_ghe}`,
          novo_cod_local: `${novoCodigo}-${ghe.nome_empresa}`
        }));
        setGhesParaAtualizar(novosGhes);
      }
    }
  };

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning'
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleOpenConfirmationModal = () => {
    if (!codigoDigitado) {
      showSnackbar('Informe o novo código do contrato', 'error');
      return;
    }
    setOpenConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(false);
  };

  const handleConfirmSave = async () => {
    setOpenConfirmationModal(false);
    setLoading(prev => ({ ...prev, enviando: true }));

    const dadosAtualizacao = {
      cnpj,
      cod_contrato_atual: codContratoAtual,
      cod_contrato_novo: codigoDigitado,
      local_obra_novo: localObraNovo,
      ghes: ghesParaAtualizar.map((ghe) => ({
        cod_ghe_atual: ghe.cod_ghe,
        cod_ghe_novo: ghe.novo_cod_ghe,
        cod_local_atual: ghe.cod_local,
        cod_local_novo: ghe.novo_cod_local
      }))
    };

    try {
      const response = await api.put('/ravim/novo-contr-emp', dadosAtualizacao);

      if (response.data?.error) {
        showSnackbar(response.data.error, 'error');
        return;
      }

      if (response.status === 200) {
        showSnackbar(
          response.data?.message || 'Contrato atualizado com sucesso!',
          'success'
        );
        setTimeout(() => history.goBack(), 2000);
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      if (error.response?.data?.error) {
        showSnackbar(error.response.data.error, 'error');
      } else {
        showSnackbar('Erro ao atualizar contrato', 'error');
      }
    } finally {
      setLoading(prev => ({ ...prev, enviando: false }));
    }
  };

  const handleSair = () => {
    history.goBack();
  };

  if (loading.inicial) {
    return (
      <Container maxWidth="lg" className={classes.root}>
        <Paper className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            Carregando dados...
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Paper className={classes.paperContainer}>
        {/* Cabeçalho */}
        <Typography variant="h4" align="center" className={classes.headerTitle}>
          Atualização de Contrato {ghesParaAtualizar.length > 0 ? 'e GHEs' : ''}
        </Typography>

        {/* Seção de Informações da Empresa */}
        <Box className={classes.section}>
          <Typography
            variant="h6"
            style={{
              marginBottom: '16px',
              fontWeight: 600,
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <BusinessIcon color="primary" />
            Informações da Empresa
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="CNPJ"
                value={cnpj}
                fullWidth
                variant="outlined"
                disabled
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#f5f5f5' }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Nome da Empresa"
                value={nomeEmpresa}
                fullWidth
                variant="outlined"
                disabled
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#f5f5f5' }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Contrato Atual"
                value={codContratoAtual}
                fullWidth
                variant="outlined"
                disabled
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#f5f5f5' }
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Seção de Atualização */}
        <Box className={`${classes.section} ${classes.sectionSecondary}`}>
          <Typography
            variant="h6"
            style={{
              marginBottom: '16px',
              fontWeight: 600,
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <UpdateIcon color="primary" />
            Atualização de Contrato
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Novo Código do Contrato*"
                value={codigoDigitado}
                onChange={handleCodigoDigitadoChange}
                fullWidth
                variant="outlined"
                required
                helperText="Digite o novo código do contrato"
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' },
                  endAdornment: codigoDigitado && (
                    <IconButton
                      size="small"
                      onClick={() => setCodigoDigitado('')}
                      edge="end"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Novo Local de Obra"
                value={localObraNovo}
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  style: { borderRadius: '8px', backgroundColor: '#f5f5f5' },
                  startAdornment: (
                    <InputAdornment position="start">
                      <InfoIcon color="action" />
                    </InputAdornment>
                  )
                }}
                helperText="Gerado automaticamente"
              />
            </Grid>
          </Grid>

          {/* Tabela de GHEs */}
          {ghesParaAtualizar.length > 0 && (
            <Box mt={4}>
              <Typography
                variant="subtitle1"
                gutterBottom
                style={{ fontWeight: 500 }}
              >
                GHEs que serão atualizados:
              </Typography>

              <Paper className={classes.tableContainer}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.compactCell}>
                        <strong>Contrato Atual</strong>
                      </TableCell>
                      <TableCell className={classes.compactCell}>
                        <strong>Local Obra</strong>
                      </TableCell>
                      <TableCell className={classes.compactCell}>
                        <strong>GHE</strong>
                      </TableCell>
                      <TableCell className={classes.compactCell}>
                        <strong>Novo Contrato</strong>
                      </TableCell>
                      <TableCell className={classes.compactCell}>
                        <strong>Novo Local</strong>
                      </TableCell>
                      <TableCell className={classes.compactCell}>
                        <strong>Novo GHE</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ghesParaAtualizar.map((ghe, index) => (
                      <TableRow
                        key={index}
                        hover
                        className={
                          index % 2 === 0 ? classes.highlightRow : undefined
                        }
                      >
                        <TableCell className={classes.compactCell}>
                          {ghe.cod_contrato}
                        </TableCell>
                        <TableCell className={classes.compactCell}>
                          {ghe.cod_local}
                        </TableCell>
                        <TableCell className={classes.compactCell}>
                          {ghe.nome_ghe}
                        </TableCell>
                        <TableCell className={classes.compactCell}>
                          {ghe.novo_cod_contrato || '-'}
                        </TableCell>
                        <TableCell className={classes.compactCell}>
                          {ghe.novo_cod_local || '-'}
                        </TableCell>
                        <TableCell className={classes.compactCell}>
                          {ghe.novo_cod_ghe || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}

          {/* Mensagem quando não há GHEs */}
          {dadosCarregados && ghesParaAtualizar.length === 0 && (
            <Box mt={4} p={2} bgcolor="#fff8e1" borderRadius={4}>
              <Typography
                variant="body1"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <WarningIcon style={{ marginRight: 8, color: '#ff9800' }} />
                Nenhum GHE cadastrado para este contrato - apenas o contrato será atualizado
              </Typography>
            </Box>
          )}

          {/* Botões de ação */}
          <Box className={classes.buttonGroup}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleSair}
              style={{
                color: '#5f6c7b',
                borderColor: '#e0e0e0',
                padding: '8px 24px',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500
              }}
              disabled={loading.inicial || loading.enviando}
            >
              Voltar
            </Button>
            <ButtonAdd
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleOpenConfirmationModal}
              disabled={loading.inicial || loading.enviando || !codigoDigitado}
            >
              {loading.enviando ? (
                <>
                  <CircularProgress size={24} style={{ marginRight: 8, color: '#fff' }} />
                  Salvando...
                </>
              ) : (
                'Confirmar Atualização'
              )}
            </ButtonAdd>
          </Box>
        </Box>
      </Paper>

      {/* Modal de Confirmação */}
      <Dialog
        open={openConfirmationModal}
        onClose={handleCloseConfirmationModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          style={{
            backgroundColor: '#0D9C5B',
            color: '#fff',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            padding: '16px 24px'
          }}
        >
          <Box display="flex" alignItems="center">
            <InfoIcon
              style={{
                color: '#fff',
                fontSize: '1.5rem',
                marginRight: '8px'
              }}
            />
            <Typography
              variant="h5"
              style={{
                fontWeight: 'bold',
                lineHeight: '1.5'
              }}
            >
              Confirmação de Atualização
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: 24 }}>
          <Box mt={2} mb={3}>
            <Typography variant="body1" paragraph>
              Você está prestes a atualizar o contrato{' '}
              <strong>{codContratoAtual}</strong> para <strong>{codigoDigitado}</strong>.
              {ghesParaAtualizar.length > 0 && (
                <>
                  {' '}e <strong>{ghesParaAtualizar.length} GHE(s)</strong>.
                </>
              )}
            </Typography>

            <Box p={2} bgcolor="#f9f9f9" borderRadius={4}>
              <Typography
                variant="subtitle1"
                gutterBottom
                style={{ fontWeight: 'bold' }}
              >
                Resumo das alterações:
              </Typography>
              <Divider style={{ marginBottom: 12 }} />
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>CNPJ:</strong> {cnpj}
                  </Typography>
                  <Typography>
                    <strong>Empresa:</strong> {nomeEmpresa}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Contrato Atual:</strong> {codContratoAtual}
                  </Typography>
                  <Typography>
                    <strong>Novo Contrato:</strong> {codigoDigitado}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    <strong>Novo Local de Obra:</strong> {localObraNovo}
                  </Typography>
                </Grid>
                {ghesParaAtualizar.length > 0 && (
                  <Grid item xs={12}>
                    <Typography>
                      <strong>GHEs a atualizar:</strong> {ghesParaAtualizar.length}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box mt={3} p={2} bgcolor="#fff8e1" borderRadius={4}>
              <Typography
                variant="body2"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <WarningIcon style={{ color: '#ff9800', marginRight: 8 }} />
                Esta ação não pode ser desfeita. Verifique cuidadosamente os
                dados antes de confirmar.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0' }}
        >
          <Button
            onClick={handleCloseConfirmationModal}
            variant="outlined"
            style={{
              color: '#5f6c7b',
              borderColor: '#e0e0e0',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            style={{
              backgroundColor: '#0D9C5B',
              color: '#fff',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none'
            }}
            startIcon={<SaveIcon />}
            disabled={loading.enviando}
          >
            {loading.enviando ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Confirmar'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de Feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        style={{ marginTop: '80px' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          style={{
            borderRadius: '8px',
            alignItems: 'center',
            fontWeight: 500
          }}
          iconMapping={{
            success: <CheckCircleIcon fontSize="inherit" />,
            warning: <WarningIcon fontSize="inherit" />,
            error: <ErrorIcon fontSize="inherit" />
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {snackbarSeverity === 'success' ? (
              <CheckCircleIcon style={{ marginRight: '8px' }} />
            ) : snackbarSeverity === 'warning' ? (
              <WarningIcon style={{ marginRight: '8px', color: '#ff9800' }} />
            ) : (
              <ErrorIcon style={{ marginRight: '8px' }} />
            )}
            {snackbarMessage}
          </div>
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NovoContratoEmpresas;